/**
 * Pax content (CMS) — useContent hook + ContentProvider.
 *
 * Sprint 3 of the post-launch overhaul.
 *
 * Hydrates the dictionary ONCE per session from the `pax-content` edge
 * function (which is itself CDN-cached for 5 minutes). Components call
 * `useContent('dashboard.greeting', 'Welcome back')` — the hook returns
 * the stored value when present, or the hard-coded fallback when missing.
 *
 * Design rule: every call must include a fallback string. If the dictionary
 * is empty, the platform still renders correctly — no flashing of empty
 * labels while content loads.
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type ContentDict = Record<string, string>;

interface ContentContextValue {
  /** Resolved key → value map (only keys present in the DB are here). */
  content: ContentDict;
  /** Whether the dictionary has loaded at least once. */
  loaded: boolean;
  /** Re-fetch (used by the staff CMS editor after a save). */
  refresh: () => Promise<void>;
}

const ContentContext = createContext<ContentContextValue>({
  content: {},
  loaded: false,
  refresh: async () => {},
});

interface ProviderProps {
  children: ReactNode;
  /** Optional locale override; defaults to 'en'. */
  locale?: string;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

async function fetchContent(locale: string): Promise<ContentDict> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return {};
  try {
    const r = await fetch(`${SUPABASE_URL}/functions/v1/pax-content?locale=${encodeURIComponent(locale)}`, {
      headers: { Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
    });
    if (!r.ok) return {};
    const data = (await r.json()) as { success?: boolean; content?: ContentDict };
    return data?.success && data.content ? data.content : {};
  } catch {
    return {};
  }
}

export function ContentProvider({ children, locale = 'en' }: ProviderProps) {
  const [content, setContent] = useState<ContentDict>({});
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    const dict = await fetchContent(locale);
    setContent(dict);
    setLoaded(true);
  }, [locale]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<ContentContextValue>(
    () => ({ content, loaded, refresh }),
    [content, loaded, refresh],
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

/**
 * Resolve a CMS-managed string. Always pass a fallback — the hook never
 * returns an empty string for missing keys, but the fallback is what
 * renders while the dictionary is hydrating (and forever if the key is
 * missing from the DB).
 *
 * Example:
 *   <h1>{useContent('dashboard.greeting', 'Welcome back')}</h1>
 */
export function useContent(key: string, fallback: string): string {
  const { content } = useContext(ContentContext);
  return content[key] ?? fallback;
}

/**
 * Bulk variant — handy when a single component needs many strings and
 * you want to avoid the per-call hook ceremony.
 *
 * Example:
 *   const t = useContentBulk();
 *   <h1>{t('dashboard.greeting', 'Welcome back')}</h1>
 */
export function useContentBulk(): (key: string, fallback: string) => string {
  const { content } = useContext(ContentContext);
  return useCallback((key: string, fallback: string) => content[key] ?? fallback, [content]);
}

export function useContentContext(): ContentContextValue {
  return useContext(ContentContext);
}
